import { logger } from '@tuic/logger';

import { getLooselyTypedUtag } from '../src/tracking/tealium-helpers';

import type {
  TealiumViewDataObject,
  TealiumLinkDataObject,
  TealiumEvent,
} from '../tracking/tealium';

/**
 * Tealium is the data layer for Google Analytics and other data tracking services to track user activity
 * @link: https://docs.tealium.com/platforms/javascript/track/
 *
 * The hooks in this file have two purposes:
 * - Tracks new page views using the Universal Data Object (utag_data), defined in this doc: https://docs.tealium.com/platforms/javascript/universal-data-object/
 * - Tracks user events such as non-page views, page interactions, and other dynamic page events
 *
 * Returns a function for tracking user events.
 */
const useTealium = (): {
  trackView: (_viewData: TealiumViewDataObject) => void;
  trackEvent: (_linkData: TealiumLinkDataObject) => void;
} => {
  // Get environment flag
  const tealiumEnv = process.env.NEXT_PUBLIC_TEALIUM_ENV_FLAG || 'prod';

  // Get customers device (viewport width)
  const getCustomerDevice = () => {
    return window.innerWidth < 570 ? 'm' : 'd';
  };

  // Get page name by window location (without the query parameters)
  // e.g. 'www_meinschiff_com|de|kreuzfahrt-suchen|reiseziele|nordland|',
  const getPageName = () => {
    const location = window.location;
    const host = location.host.replaceAll('.', '_'); // 'www_meinschiff_com'
    const slug = location.pathname
      .replace('/de/', '/')
      .replace('/en/', '/')
      .split('/')
      .slice(1)
      .filter(v => v); // Remove empty values
    return [...[host], ...slug].join('|'); // 'www_meinschiff_com|de|reiseplanung|merkliste'
  };

  // Get query paramenter (without '?')
  const getPageUrlParameter = () => {
    return document.location.search.replace('?', ''); // 'foo=bar'
  };

  // Format the label ('Mein Schiff Logo' becomes 'mein-schiff-logo', not 'mein_schiff-logo')
  // @see tracking concept
  const formatValue = (label: string) => {
    return label.replace(/\s+/g, '-').toLowerCase();
  };

  /**
   * Track page view
   *
   * Page view tracking gathers information about the navigation path a visitor takes.
   * This effect should fire on each page view or if something changes about logged in user.
   *
   * ```tsx
   * const { trackView } = useTealium();
   * trackView({
   *   tealium_event: 'user_login', // optional
   *   page_title: 'Kreuzfahrt buchen',
   *   page_type: 'destinations',
   * });
   * ```
   */
  const trackView = (viewData: { tealium_event?: TealiumEvent }) => {
     
    const utag = getLooselyTypedUtag(window);

    const data: TealiumViewDataObject = {
      customer_device: getCustomerDevice(),
      page_title: document.title,
      page_type: 'other',

      // GA 4
      page_name: getPageName(),
      page_platform: 'reiseplanung', // Default
      page_category: 'other', // Default
      page_url_parameter: getPageUrlParameter(),

      ...viewData,
    };

    // Debug logging on local dev
    if (tealiumEnv === 'dev') {
      logger.debug(`Tealium trackView: ${JSON.stringify(data)}`);
      return;
    }

    utag.view(data);
  };

  /**
   * Track event
   *
   * This tracks events such as non-page views, page interactions, and other dynamic page events.
   * Event tracking collects information about your visitors’ interactions within a page.
   *
   * ```tsx
   * const { trackEvent } = useTealium();
   * trackEvent({
   *   event_action: 'Header',
   *   event_category: 'Navigation',
   *   event_label: 'Anmelden',
   * });
   * ```
   */
  const trackEvent = (linkData: { tealium_event?: TealiumEvent }) => {
     
    const utag = getLooselyTypedUtag(window);

    const data: TealiumLinkDataObject = {
      customer_device: getCustomerDevice(),
      event_action: '', // e.g. 'Header'
      event_category: '', // e.g. 'Navigation'
      event_label: '', // e.g. 'Anmelden'
      ...linkData,
    };

    // Format the properties
    Object.keys(data).forEach(key => {
      switch (key) {
        case 'click_label':
        case 'form_name':
        case 'form_interaction':
          data[key] = formatValue(data[key]!);
          break;
      }
    });

    // Debug logging on local dev
    if (tealiumEnv === 'dev') {
      logger.debug(`Tealium trackEvent: ${JSON.stringify(data)}`);
      return;
    }

    utag.link(data);
  };

  return { trackView, trackEvent };
};

export { useTealium };
